/**
 * フォント設定コンテキスト
 * 
 * サイト全体のフォント設定を管理するコンテキスト。
 * Google Fonts APIからフォント一覧を取得し、
 * ユーザーが選択したフォントを適用・永続化します。
 */
import { createContext, useContext, useState, useEffect, useCallback, type ReactNode, type FC } from 'react';
import {
  type GoogleFont,
  type FontSubset,
  SYSTEM_FONT,
  POPULAR_JAPANESE_FONTS,
  POPULAR_LATIN_FONTS,
  fetchGoogleFonts,
  loadGoogleFont,
  applyFont,
  saveFontToStorage,
  loadFontFromStorage,
} from '../api/googleFonts';

/**
 * フォントコンテキストの型定義
 */
interface FontContextType {
  /** 日本語フォント一覧 */
  japaneseFonts: GoogleFont[];
  /** 英語フォント一覧 */
  latinFonts: GoogleFont[];
  /** 現在選択されているフォント */
  selectedFont: GoogleFont;
  /** フォント一覧を読み込み中かどうか */
  isLoading: boolean;
  /** フォントを選択・適用する */
  setFont: (font: GoogleFont) => void;
  /** フォント一覧を再読み込みする */
  refreshFonts: () => Promise<void>;
}

const FontContext = createContext<FontContextType | undefined>(undefined);

/**
 * フォントプロバイダーコンポーネント
 */
export const FontProvider: FC<{ children: ReactNode }> = ({ children }) => {
  // フォント一覧（初期値はプリセット）
  const [japaneseFonts, setJapaneseFonts] = useState<GoogleFont[]>(POPULAR_JAPANESE_FONTS);
  const [latinFonts, setLatinFonts] = useState<GoogleFont[]>(POPULAR_LATIN_FONTS);
  
  // 選択中のフォント（初期値はシステムフォント）
  const [selectedFont, setSelectedFont] = useState<GoogleFont>(SYSTEM_FONT);
  
  // 読み込み状態
  const [isLoading, setIsLoading] = useState(false);

  /**
   * フォント一覧を取得
   */
  const refreshFonts = useCallback(async () => {
    setIsLoading(true);
    try {
      const [jpFonts, enFonts] = await Promise.all([
        fetchGoogleFonts('japanese'),
        fetchGoogleFonts('latin'),
      ]);
      setJapaneseFonts(jpFonts);
      setLatinFonts(enFonts);
    } catch (error) {
      console.error('Failed to fetch fonts:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * フォントを選択・適用
   */
  const setFont = useCallback((font: GoogleFont) => {
    // フォントをロード（システムフォント以外）
    loadGoogleFont(font);
    
    // CSS変数を更新
    applyFont(font);
    
    // 状態を更新
    setSelectedFont(font);
    
    // localStorageに保存
    saveFontToStorage(font);
  }, []);

  /**
   * 初期化: localStorageから設定を復元し、フォント一覧を取得
   */
  useEffect(() => {
    // localStorageから保存されたフォントを復元
    const savedFont = loadFontFromStorage();
    if (savedFont) {
      // フォントをロード
      loadGoogleFont(savedFont);
      // CSS変数を更新
      applyFont(savedFont);
      // 状態を更新
      setSelectedFont(savedFont);
    }

    // フォント一覧を取得
    refreshFonts();
  }, [refreshFonts]);

  const value: FontContextType = {
    japaneseFonts,
    latinFonts,
    selectedFont,
    isLoading,
    setFont,
    refreshFonts,
  };

  return <FontContext.Provider value={value}>{children}</FontContext.Provider>;
};

/**
 * フォントコンテキストを使用するカスタムフック
 */
export const useFont = (): FontContextType => {
  const context = useContext(FontContext);
  if (context === undefined) {
    throw new Error('useFont must be used within a FontProvider');
  }
  return context;
};

export { SYSTEM_FONT };
export type { GoogleFont, FontSubset };
